import React from 'react'
import {get, isEmpty, isNil, map, set, sortBy} from 'lodash'
import _Date from '../containers/date'
import {getFullAddress} from './locations'
import FinancialHelper, {getCurrency, getCurrencySymbol} from 'helpers/financial'
import {removeFieldsForPricingGroup} from 'helpers/contacts'
import {activityOwner} from 'helpers/contactFields'
import {getIsEmailValidName} from 'helpers/emailing'
import {getDateString} from 'helpers/dates'
import {nonNull} from 'helpers/functional'
import {features, permissions, USER_FIELD_TYPES} from 'helpers/constants'
import {t} from "helpers/language"
import {getBooleanLabel} from "helpers/pivotTable"
import Contacts, {
  getContactFullName,
  getSubscriptionTypeLabel,
  isIndividual,
  parsePostCode,
  REFERRAL_TYPE,
} from './contacts'

export const ContactField = {
  FORENAME: 'f_forename',
  SURNAME: 'f_surname',
  MOBILE: 'f_mobile',
  PHONE: 'f_phone',
  EMAIL: 'f_email',
  TYPE: 'f_type',
  ACCOUNT_MANAGER: 'f_accountManagerId',
  DEPARTMENTS: 'f_departments',
  CONTACT_METHODS: 'f_contactMethods',
  SOURCE: 'f_source',
  VALID_EMAIL: 'f_validEmail',
  EMAIL_STATUS: 'f_emailStatus',
  EMAIL_SUB_STATUS: 'f_emailSubStatus',
  EMAIL_STATUS_MODIFIER_ID: 'f_emailStatusModifierId',
  EMAIL_STATUS_MODIFIED_DATE: 'f_emailStatusModifiedDate',
}

export const CompanyField = {
  NAME: 'c_name',
  REFERRAL_COMPANY_ID: 'c_referralCompanyId',
  REFERRAL_TYPE: 'c_referralType',
  REFERRAL_VALUE: 'c_referralValue',
  COVER_REFERRAL: 'c_coverReferral',
  WEBSITE: 'c_website',
  PRICING_GROUP: 'c_pricingGroup',
  ACCOUNTING_STATUS: 'c_accountingStatus',
  ACCOUNTING_ON_HOLD: 'c_accountingOnHold',
  ACCOUNTING_BALANCE: 'c_accountingBalance',
  ACCOUNTING_CREDIT_LIMIT: 'c_accountingCreditLimit',
  ACCOUNTING_AVAILABLE_CREDIT: 'c_accountingAvailableCredit',
  PAYMENT_TERMS: 'c_paymentTerms',
  QUOTE_TERMS: 'c_quoteTerms',
  PURCHASE_TERMS: 'c_purchaseTerms',
}

export const LocationField = {
  PHONE: 'l_phone',
}

export const DataType = {
  LABEL: 'Label',
  STRING: 'String',
  EMAIL: 'Email',
  PHONE: 'Phone',
  MOBILE: 'Mobile',
  NUMBER: 'Number',
  BOOLEAN: 'Boolean',
  LIST: 'List',
  DATE: 'Date',
  TIME: 'Time',
  DATE_TIME: 'Date Time',
  DATE_RANGE: 'Date Range',
  MULTI_LIST: 'Multi List',
  LOOKUP: 'Lookup',
  MEMO: 'Memo',
  CURRENCY: 'Currency',
  COMPANY_LIST: 'Company List',
  CONTACT_LIST: 'Contact List',
  OPPORTUNITY_LIST: 'Opportunity List',
  REFERENCE_LIST: 'Reference List',
  ORDER_LIST: 'Order List',
  ORDER_SELECTOR: 'Order Selector',
  CAMPAIGN_SELECTOR: 'Campaign Selector',
  DURATION_PICKER: 'Duration Picker',
  REPEAT_DAYS: 'Repeat Days',
  REPEAT_MONTHS: 'Repeat Months',
  END_PERIOD: 'End Period',
  TIME_SPENT: 'Time Spent',
  PACK_TYPES: 'Pack Types',
  PART_CATEGORY: 'Part Category',
  CONTACT_DETAILS: 'Contact Details',
  CHECKBOX: 'CheckBox',
  CHECKBOX_WITH_TEXT: 'CheckBoxWithText',
  SEPARATOR: 'Separator',
}

const selectCompanyField = {
  active: true,
  categoryName: 'Basic',
  categoryOrder: 0,
  dataType: 'Company List',
  creatable: true,
  promptTextCreator: label => `Create New Company: "${label}"`,
  placeholder: 'No Company',
  key: 'companySelect',
  field: 'companySelect',
  fieldName: 'Company',
  type: 'field',
  label: 'Company',
  order: 1,
  user: false,
  readOnly: false,
  mandatory: false,
  selectable: false,
  fields: {
    default: true,
    locations: true,
  },
}

const defaultBooleanUserFieldOptions = {
  // NOTE: Using direct CSS variable access for boolean user fields
  // These colors should be defined in the theme CSS variables
  falseColour: typeof window !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue('--cg-color-boolean-false').trim() || '#ff0000'
    : '#ff0000',
  falseLabel: 'No',
  trueColour: typeof window !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue('--cg-color-boolean-true').trim() || '#00ff00'
    : '#00ff00',
  trueLabel: 'Yes',
}

const accountManagerApplyToMainField = {
  active: true,
  categoryName: 'Extended',
  categoryOrder: 2,
  dataType: 'Boolean',
  key: 'accountManagerApplyToMain',
  field: 'accountManagerApplyToMain',
  fieldName: 'Apply to the main contact and all new contacts',
  label: 'Apply to the main contact and all new contacts',
  order: 3,
  user: false,
  readOnly: false,
  mandatory: false,
}

const accountManagerApplyToAllField = {
  active: true,
  categoryName: 'Extended',
  categoryOrder: 2,
  dataType: 'Boolean',
  key: 'accountManagerApplyToAll',
  field: 'accountManagerApplyToAll',
  fieldName: 'Apply to all other contacts at this company',
  type: 'field',
  label: 'Apply to all other contacts at this company',
  order: 3,
  user: false,
  readOnly: false,
  mandatory: false,
}

export const categoriseFields = (fields) => {
  if (isEmpty(fields))
    return []

  const fieldList = []
  const sortedFields = sortBy(fields, ['categoryOrder', 'order'])

  map(sortedFields, field => {
    const categoryFields = fieldList[field.categoryName]
    const category = categoryFields
      ? categoryFields.slice()
      : []

    category.push(field)
    fieldList[field.categoryName] = category
  })

  return fieldList
}


const nonDetailFields = [
  ContactField.FORENAME,
  ContactField.SURNAME,
  ContactField.MOBILE,
  ContactField.PHONE,
  ContactField.EMAIL,
  CompanyField.WEBSITE,
  CompanyField.ACCOUNTING_STATUS,
  CompanyField.ACCOUNTING_ON_HOLD,
  CompanyField.ACCOUNTING_BALANCE,
  CompanyField.ACCOUNTING_CREDIT_LIMIT,
  CompanyField.ACCOUNTING_AVAILABLE_CREDIT,
]

export const getDetailFieldsOnly = (fields, includeCompanyName) => {
  const filteredFields = fields.filter(f => nonDetailFields.indexOf(f.key) < 0)
  return includeCompanyName
    ? filteredFields
    : removeField(fields, CompanyField.NAME)
}

const primaryFields = [
  'companySelect',
  ContactField.FORENAME,
  ContactField.SURNAME,
  ContactField.MOBILE,
  ContactField.PHONE,
  ContactField.EMAIL,
  CompanyField.NAME,
  'address',
  'city',
  'county',
  'postCode',
  'country',
]

export const isContactFieldVisible = (field, primaryOnly) => {
  if (field.mandatory)
    return true

  if (primaryOnly)
    return primaryFields.includes(field.key)

  return field.active
}

export const getVisibleContactFields = (fields, primaryOnly) => {
  return fields.filter(field => isContactFieldVisible(field, primaryOnly))
}

export const findField = (fields, key) => {
  return fields.find(field => field.key === key) || {}
}

export const setFieldProps = (fields, fieldKey, props) => {
  return fields.map(field => {
    return field.key === fieldKey
      ? {...field, ...props}
      : field
  })
}

export const removeField = (fields, fieldKey) => {
  return fields.filter(field => field.key !== fieldKey)
}

export const getAllFields = (
  contactFields = null,
  companyFields = null,
  includeLocationFields = true,
  countries = null,
  requiredFields = null,
  combineAddressFields = true,
  user = null) => {

  const autoCreateActivity = get(user, 'settings.autoCreateActivity', false)
  let fields = []

  if (contactFields)
    fields = [...fields, ...contactFields]

  if (companyFields)
    fields = [...fields, ...companyFields]

  if (includeLocationFields) {
    const locationFields = getLocationFields(
      countries || [],
      0,
      requiredFields,
      combineAddressFields)

    fields = [
      ...fields,
      ...(locationFields || []).map(field => ({...field, key: `l_${field.key}`})),
    ]
  }

  if (autoCreateActivity)
    fields.push(activityOwner())

  return fields
}

export const getFieldsForCompany = (params) => {
  const {
    company,
    companyFields,
    contactFields,
    countries,
    requiredFields,
    editing = true,
    isPricingGroupsEnabled,
  } = params

  let fields = removeFieldsForPricingGroup(companyFields, isPricingGroupsEnabled)
  fields = addOrRemoveFieldsForReferral(fields, company)

  return editing
    ? fields
    : addFieldsForAddingCompany(fields, contactFields, countries, requiredFields)
}

export const addFieldsForAddingCompany = (companyFields, contactFields, countries, requiredFields) => {

  companyFields = setFieldProps(companyFields, CompanyField.NAME, {categoryOrder: 0, order: 0})
  const contactFieldsForAddingCompany = getContactFieldsForAddingCompany(contactFields)
  const locationFields = getLocationFields(countries, 0.5, requiredFields)

  let fields = [
    ...companyFields,
    ...contactFieldsForAddingCompany,
    ...locationFields,
  ]

  fields = removeField(fields, 'phone')

  return fields
}

export const getContactFieldsForAddingCompany = (contactFields) => {

  let contactFieldsData = removeExcludedContactFieldsForAddingCompany(contactFields)
  contactFieldsData = setFieldProps(contactFieldsData, ContactField.FORENAME, {categoryOrder: 0, order: 0})
  contactFieldsData = setFieldProps(contactFieldsData, ContactField.SURNAME, {categoryOrder: 0, order: 0})
  contactFieldsData = setFieldProps(contactFieldsData, ContactField.PHONE, {categoryOrder: 0.2, order: 0})
  contactFieldsData = setFieldProps(contactFieldsData, ContactField.MOBILE, {categoryOrder: 0.3, order: 0})
  contactFieldsData = setFieldProps(contactFieldsData, ContactField.EMAIL, {categoryOrder: 0.35, order: 0})

  return contactFieldsData
}

const contactFieldsToIncludeWhenAddingCompany = [
  ContactField.FORENAME,
  ContactField.SURNAME,
  ContactField.EMAIL,
  ContactField.PHONE,
  ContactField.MOBILE,
]

export const removeExcludedContactFieldsForAddingCompany = (contactFields) => {
  return contactFields.filter(field =>
    field.mandatory ||
    contactFieldsToIncludeWhenAddingCompany.includes(field.key)
  )
}

export const addOrRemoveFieldsForReferral = (fields, company) => {
  if (isNil(get(company, 'referralCompanyId', null)))
    return removeReferralFields(fields)

  const referralCompanyField = findField(fields, CompanyField.REFERRAL_COMPANY_ID)
  const active = get(referralCompanyField, 'active', false)

  fields = setFieldProps(fields, CompanyField.REFERRAL_TYPE, {active})

  switch (company.referralType) {
    case REFERRAL_TYPE.value:
      return enableValueReferralFields(fields, active)

    case REFERRAL_TYPE.percentage:
      return enablePercentageReferralFields(fields, active)

    default:
      return fields
  }
}

const referralFields = [
  CompanyField.REFERRAL_TYPE,
  CompanyField.REFERRAL_VALUE,
  CompanyField.COVER_REFERRAL,
]

export const removeReferralFields = (fields) => {
  return fields.filter(field => !referralFields.includes(field.key))
}

export const enableValueReferralFields = (fields, active) => {
  const dataType = 'Currency'
  fields = setFieldProps(fields, CompanyField.REFERRAL_VALUE, {active, dataType})
  return removeField(fields, CompanyField.COVER_REFERRAL)
}

export const enablePercentageReferralFields = (fields, active) => {
  const options = {append: '%', min: 0, max: 100}
  fields = setFieldProps(fields, CompanyField.REFERRAL_VALUE, {active, options})
  return setFieldProps(fields, CompanyField.COVER_REFERRAL, {active})
}

export const getFieldsForEditingContact = (
  contact, individualFields, contactFields, isAddressMandatory, countries, requiredFields
) => {

  const baseFields = isIndividual(contact)
    ? individualFields
    : contactFields

  const chooseLocationField = getChooseLocationField(contact.company, isAddressMandatory)
  const locationFields = getLocationFields(countries, 1.5, requiredFields)

  return [
    ...baseFields,
    chooseLocationField,
    ...locationFields,
  ]
}

const emailStatusFields = [
  ContactField.EMAIL_STATUS,
  ContactField.EMAIL_STATUS_MODIFIER_ID,
  ContactField.EMAIL_STATUS_MODIFIED_DATE,
]

const emailSubStatusFields = [
  ContactField.EMAIL_SUB_STATUS,
  ContactField.EMAIL_STATUS_MODIFIER_ID,
  ContactField.EMAIL_STATUS_MODIFIED_DATE,
]

const accountingFields = [
  CompanyField.ACCOUNTING_STATUS,
  CompanyField.ACCOUNTING_ON_HOLD,
  CompanyField.ACCOUNTING_BALANCE,
  CompanyField.ACCOUNTING_CREDIT_LIMIT,
  CompanyField.ACCOUNTING_AVAILABLE_CREDIT,
]

export const removeEmailStatusFields = (fields) => {
  return fields.filter(field => !emailStatusFields.includes(field.key))
}

export const removeEmailSubStatusFields = (fields) => {
  return fields.filter(field => !emailSubStatusFields.includes(field.key))
}

export const removeAccountingFields = (fields) => {
  return fields.filter(field => !accountingFields.includes(field.key))
}

export const updateFieldsForEmail = (fields, contact) => {
  if (isEmpty(contact.email))
    return removeEmailStatusFields(fields)

  const emailField = findField(fields, ContactField.EMAIL)
  const emailStatusProps = {
    categoryOrder: emailField.categoryOrder,
    order: emailField.order + 0.5,
  }

  return setFieldProps(fields, ContactField.EMAIL_STATUS, emailStatusProps)
}

export const getCompanyFieldsForAddingContactToANewCompany = (companyFields) => {
  return companyFields.filter(field =>
    field.mandatory &&
    field.key !== CompanyField.NAME)
}

export const getFieldsForAddingContactToExistingCompany = (
  contact, countries, requiredFields, contactFields, isAddressMandatory
) => {

  const locationFields = contact.locationId === 0
    ? getLocationFields(countries, 1.5, requiredFields)
    : []
  const updatedContactFields = setAutoFocus(contactFields, 'forename')

  let fields = [
    selectCompanyField,
    ...updatedContactFields,
    accountManagerApplyToAllField,
    accountManagerApplyToMainField,
    getChooseLocationField(contact.company, isAddressMandatory),
    ...locationFields,
  ]

  fields = setFieldProps(fields, ContactField.TYPE, {readOnly: false})
  fields = removeField(fields, LocationField.PHONE)
  return updateFieldsForEmail(fields, contact)
}

export const getFieldsForAddingContactToANewCompany = (
  contactFields, companyFields, countries, requiredFields, contact
) => {

  const companyFieldsForAddingContactToANewCompany = getCompanyFieldsForAddingContactToANewCompany(companyFields)
  const locationFields = getLocationFields(countries, 1.5, requiredFields)
  const updatedContactFields = setAutoFocus(contactFields, 'forename')

  let fields = [
    selectCompanyField,
    ...updatedContactFields,
    ...companyFieldsForAddingContactToANewCompany,
    ...locationFields,
  ]

  fields = removeField(fields, ContactField.TYPE)
  fields = removeField(fields, LocationField.PHONE)
  return updateFieldsForEmail(fields, contact)
}

export const getFieldsForAddingIndividual = (individualFields, countries, requiredFields, contact) => {

  const locationFields = getLocationFields(countries, 1.5, requiredFields)
  const companyNameRemoved = removeField(individualFields, CompanyField.NAME)
  const autoFocusAdded = setAutoFocus(companyNameRemoved, 'forename')

  let fields = [
    selectCompanyField,
    ...autoFocusAdded,
    ...locationFields,
  ]

  fields = removeField(fields, ContactField.TYPE)
  fields = removeField(fields, LocationField.PHONE)
  fields = removeField(fields, 'phone')
  return updateFieldsForEmail(fields, contact)
}

export const getContactFields = (params) => {
  const {
    contact,
    companyFields,
    contactFields,
    individualFields,
    countries,
    requiredFields,
    editing = true,
  } = params

  const isAddressMandatory = isRequiredField(requiredFields, 'CONTACT_ADD_ADDRESS')

  if (editing)
    return getFieldsForEditingContact(
      contact,
      individualFields,
      contactFields,
      isAddressMandatory,
      countries,
      requiredFields)

  if (!isNil(get(contact, 'companyId', null)))
    return getFieldsForAddingContactToExistingCompany(
      contact,
      countries,
      requiredFields,
      contactFields,
      isAddressMandatory)

  if (!isEmpty(get(contact, 'name', '')))
    return getFieldsForAddingContactToANewCompany(
      contactFields,
      companyFields,
      countries,
      requiredFields,
      contact)

  return getFieldsForAddingIndividual(
    individualFields,
    countries,
    requiredFields,
    contact)
}

const isRequiredField = (requiredFields, fieldKey) => {
  return requiredFields && get(requiredFields, fieldKey, false)
}

const getCombinedAddressField = (categoryOrder, isAddressMandatory, active) => {
  return {
    active,
    categoryName: 'Location',
    categoryOrder,
    dataType: 'Memo',
    field: 'address',
    fieldName: 'Address',
    key: 'address',
    label: 'Address',
    type: 'field',
    options: {length: 450},
    user: false,
    order: 0,
    readOnly: false,
    mandatory: isAddressMandatory,
    selectable: false,
  }
}

export const getAddress1Field = (categoryOrder, isAddressMandatory, active) => {
  return {
    active,
    categoryName: 'Location',
    categoryOrder,
    dataType: 'Memo',
    field: 'address1',
    fieldName: 'Address 1',
    key: 'address1',
    label: 'Address 1',
    type: 'field',
    options: {length: 150},
    user: false,
    order: 0,
    readOnly: false,
    mandatory: isAddressMandatory,
    selectable: false,
  }
}

export const getAddress2Field = (categoryOrder, active) => {
  return {
    active,
    categoryName: 'Location',
    categoryOrder,
    dataType: 'Memo',
    field: 'address2',
    fieldName: 'Address 2',
    key: 'address2',
    label: 'Address 2',
    type: 'field',
    options: {length: 150},
    user: false,
    order: 1,
    readOnly: false,
    mandatory: false,
    selectable: false,
  }
}

export const getAddress3Field = (categoryOrder, active) => {
  return {
    active,
    categoryName: 'Location',
    categoryOrder,
    dataType: 'Memo',
    field: 'address3',
    fieldName: 'Address 3',
    key: 'address3',
    label: 'Address 3',
    type: 'field',
    options: {length: 150},
    user: false,
    order: 2,
    readOnly: false,
    mandatory: false,
    selectable: false,
  }
}

export const getCityField = (categoryOrder, requiredFields) => {
  return {
    active: true,
    categoryName: 'Location',
    categoryOrder,
    dataType: 'String',
    field: 'city',
    fieldName: 'City',
    key: 'city',
    label: 'City',
    type: 'field',
    options: {length: 150},
    user: false,
    order: 3,
    readOnly: false,
    mandatory: isRequiredField(requiredFields, 'CONTACT_ADD_CITY'),
    selectable: false,
  }
}

export const getCountyField = (categoryOrder, requiredFields) => {
  return {
    active: true,
    categoryName: 'Location',
    categoryOrder,
    dataType: 'String',
    field: 'county',
    fieldName: 'County',
    key: 'county',
    label: 'County',
    type: 'field',
    options: {length: 150},
    user: false,
    order: 4,
    readOnly: false,
    mandatory: isRequiredField(requiredFields, 'CONTACT_ADD_COUNTY'),
    selectable: false,
  }
}

export const getPostCodeField = (categoryOrder, requiredFields) => {
  return {
    active: true,
    categoryName: 'Location',
    categoryOrder,
    dataType: 'String',
    field: 'postCode',
    fieldName: 'Post Code',
    key: 'postCode',
    label: 'Post Code',
    type: 'field',
    options: {length: 150},
    user: false,
    order: 5,
    readOnly: false,
    mandatory: isRequiredField(requiredFields, 'CONTACT_ADD_POST_CODE'),
    selectable: false,
  }
}

export const getCountryField = (categoryOrder, countries, requiredFields) => {
  return {
    active: true,
    categoryName: 'Location',
    categoryOrder,
    dataType: 'List',
    field: 'country',
    fieldName: 'Country',
    key: 'country',
    label: 'Country',
    type: 'field',
    values: countries,
    user: false,
    order: 6,
    readOnly: false,
    mandatory: isRequiredField(requiredFields, 'CONTACT_ADD_COUNTRY'),
    selectable: false,
  }
}

export const getLocationPhoneField = () => {
  return {
    active: true,
    categoryName: 'Location',
    categoryOrder: 0.25,
    dataType: 'String',
    field: 'phone',
    fieldName: 'Phone',
    key: 'phone',
    label: 'Telephone',
    type: 'field',
    options: {length: 150},
    user: false,
    order: 7,
    readOnly: false,
    selectable: false,
  }
}

export const getLocationFields = (
  countries,
  categoryOrder = 0,
  requiredFields = null,
  combineAddressFields = true
) => {

  const isAddressMandatory = isRequiredField(requiredFields, 'CONTACT_ADD_ADDRESS')

  return [
    getCombinedAddressField(categoryOrder, isAddressMandatory, combineAddressFields),
    getAddress1Field(categoryOrder, isAddressMandatory, !combineAddressFields),
    getAddress2Field(categoryOrder, !combineAddressFields),
    getAddress3Field(categoryOrder, !combineAddressFields),
    getCityField(categoryOrder, requiredFields),
    getCountyField(categoryOrder, requiredFields),
    getPostCodeField(categoryOrder, requiredFields),
    getCountryField(categoryOrder, countries, requiredFields),
    getLocationPhoneField(),
  ].filter(field => field.active)
}

export const getLocationOptionLabel = (location, address) => {
  return isEmpty(address)
    ? location.name
    : `${location.name} (${address})`
}

export const getLocationOption = (location) => {
  const address = getFullAddress(location)
  const label = getLocationOptionLabel(location, address)

  return {
    value: location.id,
    label,
  }
}

const getChooseLocationField = (company, mandatory) => {
  const locations = get(company, 'locations', [])

  const locationOptions = [
    {value: 0, label: 'Add New Address'},
    ...locations.map(getLocationOption),
  ]

  return {
    active: true,
    categoryName: 'Location',
    categoryOrder: 1.5,
    dataType: 'List',
    field: 'locationId',
    fieldName: 'Address',
    key: 'locationId',
    label: 'Address',
    type: 'field',
    values: locationOptions,
    user: false,
    order: 0,
    readOnly: false,
    mandatory: !!mandatory,
    selectable: false,
  }
}

const listFieldTypes = ['List', 'Multi List']

// Returns the appropriate values for this list field, either taken from
// the field definition or from all field options loaded into the state
export const getListFieldValues = (field, allFieldOptions) => {
  if (field.values)
    return field.values.map(mapFieldValue)

  if (allFieldOptions) {
    const options = allFieldOptions.find(opt => opt.key === field.key)
    if (options && options.values)
      return options.values.map(mapFieldValue)
  }

  return null
}

const mapFieldValue = (value) => ({
  key: value.value,
  label: value.label,
  colour: value.colour,
  icon: value.icon,
  iconColour: value.iconColour,
  iconSize: value.iconSize,
})

// Returns a list of keys to load values for,
// i.e. list fields with no values in their definition
export const getFieldKeysToLoad = (fields, excludedFields = []) => {
  return fields
    .filter(field => (field.active && listFieldTypes.indexOf(field.dataType) >= 0 && isNil(field.values)))
    .map(field => field.key)
    .filter(key => excludedFields.indexOf(key) < 0)
}

// Populates the provided field definitions with props required for display
// This includes the value and possible values for list fields
export const populateFields = (fields, source, allFieldOptions) => {
  return fields.map(field => populateField(field, source, allFieldOptions))
}

const populateField = (field, source, allFieldOptions) => {
  let values, loading = false
  if (listFieldTypes.indexOf(field.dataType) >= 0) {
    values = getListFieldValues(field, allFieldOptions)
    if (values === null) {
      loading = true
      values = []
    }
  }

  return {
    ...field,
    value: getEditFieldValue(source, field),
    values,
    loading,
    subField: field.subField ? populateField(field.subField, source, allFieldOptions) : null,
  }
}

// Updates the source entity with the new value for the specified field
export const setEditFieldValue = (user, source, field, value, meta) => {
  const ret = {...source}
  if (field.user) {
    setEditUserFieldValue(user, ret, field, value)
  } else {
    setEditStandardFieldValue(user, ret, field, value, meta)
  }
  return ret
}

// Translates a field from a source entity into the correct on-screen format (see EditableValue)
export const getEditFieldValue = (source, field) => {
  if (field.user) return getUserEditFieldValue(source, field)
  return getStandardEditFieldValue(source, field)
}

export const editDepartments = (value, source, field) => {
  if (value && value.length > 0) {
    value = value.join(';')

  } else {
    value = null
  }

  updateField(source, field, value)
}

export const editEmailStatus = (value, source, user, field) => {
  if (!isNil(value) && source.emailStatus !== Contacts.SUBSCRIPTION_TYPE.recipientOptOut) {

    source.emailStatus = value
      ? Contacts.SUBSCRIPTION_TYPE.userOptIn
      : Contacts.SUBSCRIPTION_TYPE.userOptOut

    source.emailStatusModifiedDate = new Date().getTime()
    source.emailStatusModifierId = user.contactId
  }

  updateField(source, field, value)
}

export const editContactMethods = (source, value, user, field) => {
  if (source.contactmethods) {
    source.contactmethods.forEach(a => {
      const subscribed = value && value.includes(a.contactMethodId)
      if (a.subscribed !== subscribed) {
        a.subscribed = subscribed
        a.modifierId = user.contactId
        a.modifiedDate = new Date().getTime()
      }
    })
  }

  updateField(source, field, value)
}

export const editSelectedCompany = (source, meta, value) => {
  source.locationId = null
  source.phone = null

  if (meta && meta.company) {
    source.companyId = value
    source.company = meta.company

  } else {
    source.companyId = null
    source.company = null

    if (!isEmpty(value)) {
      source.name = value

    } else {
      source.name = null
    }
  }
}

export const editReferralCompany = (value, source, meta) => {
  if (value && value.value) {
    source.referralCompanyId = value.value
    source.referralcompany = meta ? meta.company : {id: value.value, name: value.label}

  } else {
    source.referralCompanyId = value
    source.referralcompany = meta ? meta.company : null
  }
}

export const editLocationId = (source, value, field) => {
  source.addNewLocation = (!isNil(value) && value === 0)

  if (source.addNewLocation)
    source.locationId = null

  updateField(source, field, value)
}

// Sets a standard field value in the source
const setEditStandardFieldValue = (user, source, field, value, meta) => {

  switch (field.key) {
    case ContactField.DEPARTMENTS:
      editDepartments(value, source, field)
      return

    case ContactField.EMAIL_STATUS:
      editEmailStatus(value, source, user, field)
      return

    case ContactField.CONTACT_METHODS:
      editContactMethods(source, value, user, field)
      return

    case 'companySelect':
      editSelectedCompany(source, meta, value)
      return

    case CompanyField.REFERRAL_COMPANY_ID:
      editReferralCompany(value, source, meta)
      return

    case 'locationId':
      editLocationId(source, value, field)
      return

    default:
      updateField(source, field, value)
      return
  }
}

export const updateField = (source, field, value) => {
  set(source, field.field, value)
}

// Translates a value from an EditableValue into the correct format for the source entity
export const setEditUserFieldValue = (user, source, field, value) => {
  const isKeyNotInitialized = (key) => {
    return isNil(source[key]) ||
      (Array.isArray(source[key]) && source[key].length === 0)
  }

  const setFieldValue = (key) => {
    source[key][field.id] = {tagId: field.id, value}
  }

  if (isKeyNotInitialized('userfields'))
    source.userfields = {}

  if (isKeyNotInitialized('userFieldValues'))
    source.userFieldValues = {}

  setFieldValue('userfields')
  setFieldValue('userFieldValues')
}

export const getFieldValue = (source, field) => {
  return get(source, field.field, null)
}

export const getDepartments = (source, field) => {
  const value = getFieldValue(source, field)

  return isEmpty(value)
    ? null
    : value.split(';')
}

export const getSelectedCompany = (source) => {
  if (source.company) {
    return {
      value: source.company.id,
      label: source.company.name,
    }
  }

  if (source.name) {
    return {
      value: source.name,
      label: source.name,
    }
  }

  return null
}

export const getReferralCompany = (source) => {
  if (source.referralcompany) {
    return {
      value: source.referralcompany.id,
      label: source.referralcompany.name,
    }
  }

  return null
}

export const getPostCode = (source, field) => {
  const value = getFieldValue(source, field)
  return parsePostCode(value)
}

export const getStandardEditFieldValue = (source, field) => {
  switch (field.key) {

    case ContactField.VALID_EMAIL:
      return getIsEmailValidName(source)

    case ContactField.DEPARTMENTS:
      return getDepartments(source, field)

    case 'companySelect':
      return getSelectedCompany(source)

    case CompanyField.REFERRAL_COMPANY_ID:
      return getReferralCompany(source)

    case 'postCode':
      return getPostCode(source, field)

    default:
      return getFieldValue(source, field)
  }
}

// Gets a user field value from the source
export const getUserEditFieldValue = (source, field) => {
  const valueFromUserFields = get(source, `userfields.${field.id}.value`)
  const valueFromOrderUserFields = get(source, `order.userfields.${field.id}.value`)
  return !isNil(valueFromUserFields) ? valueFromUserFields : valueFromOrderUserFields
}

// Check all fields are adequately completed
export const validateFields = (source, fields) => {
  return fields
    .filter(field => field.mandatory)
    .every(field => {
      const value = getEditFieldValue(source, field)
      return !isNil(value) && (!value.hasOwnProperty('length') || isFieldLengthValid(value, field))
    })
}

export const isFieldLengthValid = (value, field) => {
  if (value.length === 0) return false
  const length = get(field, 'options.length')
  if (!length) return true
  return value.length <= length
}

export const resolveDuplicateFields = (fields) => {
  return fields.map(field => {
    let {label} = field
    const isDuplicate = fields.some(f => f.key !== field.key && f.label === label)
    if (isDuplicate) label = `${label} (${getFieldType(field)})`
    return {...field, label}
  })
}

export const getFieldType = (field) => {
  switch (field.key.charAt(0)) {
    case 'c':
      return 'Company'
    case 'l':
      return 'Location'
    case 'f': {
      switch (field.object) {
        case 'contact':
          return 'Contact'
        case 'company':
          return 'Company'
        default:
          return 'User Field'
      }
    }
    default:
      return '...'
  }
}

export const getUserFieldPivotTableColumnDefinition = (
  user,
  field,
  forEdit = false,
  userFieldsPath = null,
  keyPrefix = 'user'
) => {
  const {label, dataType, options, values, readOnly, id} = field

  const getSource = e => userFieldsPath
    ? {userfields: get(e, userFieldsPath)}
    : e

  return {
    key: `${keyPrefix}_${field.id}`,
    size: 100,
    align: 'left',
    visible: false,
    user: true,
    label,
    dataType,
    options,
    values,
    readOnly,
    data: e => {
      const source = getSource(e)
      const v = getUserEditFieldValue(source, field)
      return getUserFieldData(source, v, dataType, forEdit, id, options)
    },
    export: e => {
      const source = getSource(e)
      const v = getUserEditFieldValue(source, field)
      if (dataType === 'Boolean') {
        const trueLabel = get(options, 'trueLabel', 'Yes')
        const falseLabel = get(options, 'falseLabel', 'No')
        return !isNil(v) ? getBooleanLabel(v, trueLabel, falseLabel) : null
      } else {
        return getUserFieldData(source, v, dataType, forEdit, id, options)
      }
    },
    ...getUserFieldPivotTableColumnDefinitionForDataType(field.dataType),
    editable: forEdit,
    setData: (e, v) => {
      if (v && dataType === 'Date') v = v.getTime()
      setEditUserFieldValue(user, e, field, v)
    },
    group: e => {
      const source = getSource(e)
      const v = getUserEditFieldValue(source, field)
      return getUserFieldGroup(source, v, dataType, id, options)
    },
    groupData: e => {
      const source = getSource(e)
      const v = getUserEditFieldValue(source, field)
      return getUserFieldGroup(source, v, dataType, id, options)
    },
  }
}

export const getContactUserFieldColumnDefinition = (user, field) => ({
  ...getUserFieldPivotTableColumnDefinition(user, field, false, 'contact.userfields', 'contact_user'),
  size: 200,
})

const getUserFieldData = (data, val, dataType, forEdit, id, options = null) => {
  switch (dataType) {
    case 'Date':
      return val
        ? forEdit
          ? new Date(val)
          : <_Date>val</_Date>
        : val
    case 'Multi List':
      const list = get(data, `userfields[${id}].value`)
        || get(data, `order.userfields[${id}].value`, [])

      return val ? list.join(', ') : val
    case 'Boolean':
      if (isNil(val)) return val

      const value = get(data, `userfields[${id}].value`)
        || get(data, `order.userfields[${id}].value`, false)
      const boolOptions = options ? options : defaultBooleanUserFieldOptions
      const label = get(boolOptions, `${value}Label`, 'No')
      const colour = get(boolOptions, `${value}Colour`, '#ff0000')

      return (
        <div style={{display: 'flex', alignItems: 'center'}}>
            <span style={{
              backgroundColor: colour,
              borderRadius: '6px',
              height: '12px',
              width: '12px',
              flexShrink: 0,
            }}/>
          <span style={{marginLeft: '8px', marginRight: '17px'}}>
              {label}
            </span>
        </div>
      )
    default:
      return val
  }
}

const getUserFieldGroup = (data, val, dataType, id, options = null) => {
  switch (dataType) {
    case 'Date':
      return val ? getDateString(val) : val
    case 'Multi List':
      const list = get(data, `userfields[${id}].value`, [])

      return val ? list.join(', ') : val
    case 'Boolean':
      if (!val) return val

      const value = get(data, `userfields[${id}].value`, false)
      const boolOptions = options ? options : defaultBooleanUserFieldOptions
      return get(boolOptions, `${value}Label`, 'No')
    default:
      return val
  }
}

const getUserFieldPivotTableColumnDefinitionForDataType = (dataType) => {
  switch (dataType) {
    default:
    case 'String':
      return {align: 'left', size: 100}
    case 'Number':
      return {align: 'right', size: 50}
    case 'Boolean':
      return {}
    case 'List':
      return {}
    case 'Date':
      return {}
    case 'Multi List':
      return {}
    case 'Memo':
      return {}
    case 'Currency':
      return {}
    case 'Email':
      return {}
    case 'Phone':
      return {}
    case 'Mobile':
      return {}
    case 'Company List':
      return {}
  }
}

export const getCurrencyFieldOptions = (
  currencyId,
  currencies = [],
  accuracy = 2,
  isPrepending = false
) => {

  if (isNil(currencyId))
    return null

  const currency = getCurrency(currencyId, currencies)
  const currencySymbol = getCurrencySymbol(currencyId, currencies)

  return {
    accuracy,
    min: 0,
    step: 5,
    prepend: isPrepending
      ? FinancialHelper.getUnicodeCharacter(currency.unicodePattern)
      : null,
    append: isPrepending
      ? null
      : currencySymbol,
    isCurrency: !isNil(currencyId),
  }
}

export const getCurrencyFieldOptionsWithShortDesc = (currency, accuracy = 2) => {
  return {
    append: currency ? currency.shortDesc : ' ',
    accuracy,
    min: 0,
    valueAlign: 'right',
    fullWidth: true,
  }
}

export const getQuantityFieldOptions = () => {
  return {
    append: ' ', // creates the correct alignment for all number fields
    accuracy: 0,
    step: 1,
    min: 1,
  }
}

export const getBleedFieldOptions = (append = ' ') => {
  return {
    append,
    accuracy: 0,
    step: 1,
    min: 0,
  }
}

export const getSpacingFieldOptions = (append = ' ') => {
  return {
    append,
    accuracy: 0,
    step: 1,
    min: 0,
  }
}

export const getDiscountFieldOptions = () => {
  return getPercentageFieldOptions(null, 100)
}

export const getMarginFieldOptions = () => {
  return getPercentageFieldOptions(0, 100)
}

export const getMarkupFieldOptions = () => {
  return getPercentageFieldOptions(-100)
}

export const getUnitCostFieldOptions = (homeCurrency, pricingPrecision = 2) => {
  return {
    append: homeCurrency ? homeCurrency.shortDesc : ' ',
    accuracy: pricingPrecision,
    valueAlign: 'right',
    fullWidth: true,
    step: 5,
  }
}

export const getUnitPriceFieldOptions = (targetCurrency, pricingPrecision) => {
  return {
    append: targetCurrency ? targetCurrency.shortDesc : ' ',
    accuracy: pricingPrecision,
    valueAlign: 'right',
    fullWidth: true,
    step: 5,
  }
}

export const getPercentageFieldOptions = (min = null, max = null, accuracy = 2) => {
  return {
    append: '%',
    accuracy,
    max,
    min,
    step: 5,
  }
}

export const getSizeFieldOptions = (append) => {
  return {
    accuracy: 0,
    min: 0,
    step: 1,
    append,
  }
}

export const FieldOptionType = {
  FIELD: 'FIELD',
  BUTTON: 'BUTTON',
}

export const isFieldOptionButtonType = (option) => option === FieldOptionType.BUTTON

export const parseUserFields = (order) => {

  if ((Array.isArray(order.userfields))) {
    const userFields = {}
    order.userfields
      .forEach(userField => {
        userFields[userField.tagId] = {
          tagId: userField.tagId,
          value: userField.value,
        }
      })
    return userFields
  }

  return order.userfields
}

export const isRequiredFieldValueInvalid = (value) => {
  const isNumberValue = typeof value === 'number'
  const isBooleanValue = typeof value === 'boolean'
  const isDateValue = Object.prototype.toString.call(value) === '[object Date]'

  if (isNumberValue || isBooleanValue || isDateValue) {
    return isNil(value)
  }

  return isNil(value) || isEmpty(value)
}

export const isFieldRequired = (validateState, mandatory, value) => {
  return (validateState && mandatory) && isRequiredFieldValueInvalid(value)
}

export const getRequiredFieldProps = (validateState, mandatory, placeholder, errorText, value) => {
  const showRequired = isFieldRequired(validateState, mandatory, value)

  return {
    className: showRequired ? 'required-field' : '',
    hintText: mandatory ? 'Required' : placeholder,
    errorText: showRequired ? t('This field is mandatory') : errorText || '',
  }
}

export const getNonRemovedFields = (fieldsToRemove, fields) => {
  return fields.filter((field) => !fieldsToRemove.includes(field.key))
}

export const getActiveFields = (fields) => {
  return fields.filter(field => field.active)
}

export const setAutoFocus = (fields, autoFocusField) => {
  return fields.map(field => {
    return field.field === autoFocusField
      ? {...field, autoFocus: true}
      : field
  })
}

export const setAutoFocusField = (fields, enableAutoFocus) => {
  const anyAutoFocus = fields.some(field => field.autoFocus)
  if (anyAutoFocus && !enableAutoFocus) {
    fields.forEach(field => {
      field.autoFocus = false
    })
  } else if (!anyAutoFocus && !isEmpty(fields) && enableAutoFocus) {
    fields[0].autoFocus = true
  }
}

export const getEmailStatusDetailField = (contact, homeContacts, order, categoryOrder, categoryName) => {
  return {
    key: `emailStatusDetail`,
    label: 'Email Status',
    value: getEmailStatusDetailFieldHint(contact, homeContacts),
    active: true,
    dataType: 'String',
    readOnly: true,
    categoryOrder,
    categoryName,
    order,
  }
}

export const getEmailStatusDetailFieldHint = (contact, homeContacts) => {
  const {emailStatusModifiedDate, emailStatusModifierId} = contact
  const modifier = homeContacts.find(contact => contact.id === emailStatusModifierId)

  const modifierLabel = isNil(modifier)
    ? null
    : ` by ${getContactFullName(modifier)}`

  const modifiedLabel = isNil(emailStatusModifiedDate)
    ? null
    : ` on ${getDateString(emailStatusModifiedDate)}`

  return [
    getSubscriptionTypeLabel(contact.emailStatus),
    modifierLabel,
    modifiedLabel,
  ]
    .filter(nonNull)
    .join(' ')
}

export const getContactMethodField = (
  method, contactMethodMapping, homeContacts, order, categoryOrder, categoryName
) => {
  return {
    key: `contactMethod_${method.id}`,
    label: `${method.name} Status`,
    value: getContactMethodFieldHint(contactMethodMapping, homeContacts),
    active: true,
    dataType: 'String',
    readOnly: true,
    categoryOrder,
    categoryName,
    order,
  }
}

export const getContactMethodFieldHint = (contactMethodMapping, homeContacts) => {
  const {modifierId, modifiedDate, subscribed} = contactMethodMapping

  const modifier = isNil(modifierId)
    ? null
    : homeContacts.find(contact => contact.id === modifierId)

  const optedLabel = `Opted ${subscribed ? 'In' : 'Out'}`

  const modifierLabel = isNil(modifier)
    ? null
    : ` by ${getContactFullName(modifier)}`

  const modifiedLabel = isNil(modifiedDate)
    ? null
    : ` on ${getDateString(modifiedDate)}`

  return [optedLabel, modifiedLabel, modifierLabel]
    .filter(nonNull)
    .join(' ')
}

export const getUserFieldCategoryType = (routeType) => {
  switch (routeType.toLowerCase()) {
    case 'contacts':
      return USER_FIELD_TYPES.CONTACT_USER_FIELD
    case 'companies':
      return USER_FIELD_TYPES.COMPANY_USER_FIELD
    case 'quotes':
      return USER_FIELD_TYPES.QUOTE_USER_FIELD
    case 'quote-items':
      return USER_FIELD_TYPES.QUOTE_ITEM_USER_FIELD
    case 'purchase-orders':
      return USER_FIELD_TYPES.PURCHASE_ORDER_USER_FIELD
    case 'purchase-order-items':
      return USER_FIELD_TYPES.PURCHASE_ORDER_ITEM_USER_FIELD
  }
}

export const getUserFieldRequiredFeature = (routeType) => {
  switch (routeType.toLowerCase()) {
    case 'contacts':
      return features.CONTACT_USER_FIELDS
    case 'companies':
      return features.CONTACT_USER_FIELDS
    case 'quotes':
      return features.QUOTATION_USER_FIELDS
    case 'quote-items':
      return features.QUOTATION_USER_FIELDS
    case 'purchase-orders':
      return features.PURCHASING_USER_FIELDS
    case 'purchase-order-items':
      return features.PURCHASING_USER_FIELDS
  }
}

export const getUserFieldEditPermission = (routeType) => {
  switch (routeType.toLowerCase()) {
    case 'contacts':
      return permissions.MANAGE_TAG
    case 'companies':
      return permissions.MANAGE_TAG
    case 'quotes':
      return permissions.MANAGE_QUOTE_TAGS
    case 'quote-items':
      return permissions.MANAGE_QUOTE_TAGS
    case 'purchase-orders':
      return permissions.MANAGE_PURCHASING_TAGS
    case 'purchase-order-items':
      return permissions.MANAGE_PURCHASING_TAGS
  }
}

export const isShowInEditorUserFieldOption = (routeType) => {
  switch (routeType.toLowerCase()) {
    case 'contacts':
      return false
    case 'companies':
      return false
    case 'quotes':
      return true
    case 'quote-items':
      return false
    case 'purchase-orders':
      return true
    case 'purchase-order-items':
      return false
    default:
      return false
  }
}

export const getShowInEditorUserFieldLabel = (routeType) => {
  switch (routeType.toLowerCase()) {
    case 'quotes':
      return 'Show On Quote'
    case 'purchase-orders':
      return 'Show On Purchase Order'
    default:
      return null
  }
}

export const isUserFieldReadOnly = (tag) => {
  if (isNil(tag)) return false

  return !isNil(tag.syncSource)
}

export const showDateTimeFieldDateValue = (hideDate, showToggle, isToggled) => {
  return !hideDate && (!showToggle || isToggled)
}

export const showDateTimeFieldTimeValue = (allowNone, showToggle, isToggled, value) => {
  const isNoneSelected = allowNone && isNil(value)
  if (isNoneSelected) return false
  return !showToggle || isToggled
}
